import React, { useState } from "react";
import { Button, Modal, Pagination, Skeleton } from "@mui/material";
import styles from "./AddFeaturedItem.module.scss";
import { InputText } from "@/components/InputText/InputText";
import { useForm } from "react-hook-form";
import searchIcon from "@/assets/icons/search.svg";
import { VideoCard } from "@/components/VideoCard/VideoCard";
import { AudioCard } from "@/components/AudioCard/AudioCard";
import {
  useGetMediaMusicQuery,
  useGetMediaVideoQuery,
} from "@/store/rtk-api/media.api";

const ITEMS_PER_PAGE = 9;

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  type: "video" | "music";
};

export const AddFeaturedItem = ({ isOpen, setIsOpen, type }: Props) => {
  const { control, watch } = useForm();
  const [page, setPage] = useState(1);
  const searchInput = watch("search");

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const {
    data: videoData,
    isLoading,
    isSuccess,
  } = useGetMediaVideoQuery({
    page,
    limit: ITEMS_PER_PAGE,
    sortByDate: "ASC",
    searchTerm: searchInput,
  });

  const {
    data: musicData,
    isLoading: isLoadingMusic,
    isSuccess: isSuccessMusic,
  } = useGetMediaMusicQuery({
    page,
    limit: ITEMS_PER_PAGE,
    searchTerm: searchInput,
  });

  return (
    <Modal
      aria-labelledby="modal add item to recommended"
      open={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <div className={styles.modalContainer}>
        <h3 className={styles.modalTitle}>Add item</h3>
        <div className={styles.modalInput}>
          <InputText
            label="Search"
            control={control}
            name="search"
            icon={searchIcon}
            onChange={() => {}}
          />
        </div>
        <div className={styles.modalContent}>
          <div className={styles.cards}>
            {type === "video" ? (
              <>
                {isLoading &&
                  Array(2)
                    .fill(0)
                    .map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="rounded"
                        width="100%"
                        height="400px"
                        className={styles.skeletonItem}
                      />
                    ))}
                {isSuccess &&
                  videoData.media.map((video) => (
                    <VideoCard
                      key={video.uid}
                      video={video}
                      isEdit={false}
                      isFeatured
                    />
                  ))}
              </>
            ) : (
              <>
                {isLoadingMusic &&
                  Array(3)
                    .fill(0)
                    .map((_, index) => (
                      <div className={styles.item} key={index}>
                        <div className={styles.itemInfoWrap}>
                          <Skeleton
                            variant="rectangular"
                            width={480}
                            height={150}
                            animation="wave"
                            sx={{ borderRadius: "8px" }}
                          />
                        </div>
                      </div>
                    ))}
                {isSuccessMusic &&
                  musicData.media.map((audio) => (
                    <AudioCard
                      key={audio.uid}
                      audio={audio}
                      isEdit={false}
                      isFeatured
                    />
                  ))}
              </>
            )}
          </div>
          <div className={styles.btns}>
            {type === "video" && videoData ? (
              <Pagination
                page={page}
                onChange={handlePageChange}
                count={Math.ceil(videoData.total / ITEMS_PER_PAGE)}
              />
            ) : (
              musicData && (
                <Pagination
                  page={page}
                  onChange={handlePageChange}
                  count={Math.ceil(musicData.total / ITEMS_PER_PAGE)}
                />
              )
            )}

            <Button
              sx={{
                textTransform: "capitalize",
                height: "44px",
                width: "130px",
                backgroundColor: "#008000",
                border: "none",
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#FF0000",
                },
              }}
              variant="contained"
              onClick={() => setIsOpen(false)}
            >
              Save
            </Button>

            <Button
              variant="outlined"
              sx={{
                textTransform: "capitalize",
                height: "44px",
                width: "130px",
                border: "1px solid #5570F1",
                color: "#5570F1",
                borderRadius: "12px",
              }}
              onClick={() => setIsOpen(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

